import { cn } from '@kalos/ui';
import { type ReactNode } from 'react';

type Tone = 'neutral' | 'info' | 'success' | 'warning';

const toneClasses: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  info: 'bg-info text-info-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
};

/** A status as a small tinted pill, placed at the start of a row's line. */
export const RowPill = ({ tone, children }: { tone: Tone; children: ReactNode }) => (
  <span
    className={cn(
      'mr-2 inline-flex items-center rounded-full px-2 py-0.5 align-middle text-xs font-medium',
      toneClasses[tone],
    )}
  >
    {children}
  </span>
);

/**
 * One result in a phone search list: jobs, customers, properties and contracts all use it, so
 * they read the same. The title wraps to two lines; each fact gets its own line, which wraps
 * rather than being cut off mid-word. The whole row is the tap target. One optional control sits
 * in the top-right corner beside the title only, so the lines below keep the full width.
 */
export const SearchResultRow = ({
  title,
  lines,
  actionLabel,
  onSelect,
  disabled = false,
  hint,
  corner,
}: {
  title: string;
  /** One fact or short pair per line; empty ones are left out. */
  lines: ReactNode[];
  /** Read before the title by screen readers: "Open job", "Select". */
  actionLabel: string;
  onSelect: () => void;
  disabled?: boolean;
  /** A word beside the title saying what tapping does, such as "Select" in a picker. */
  hint?: string;
  /** One control in the top-right corner, such as a "More" menu. */
  corner?: ReactNode;
}) => (
  <li className="after:bg-border relative after:absolute after:bottom-0 after:left-4 after:right-0 after:h-px last:after:hidden">
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-label={`${actionLabel}: ${title}`}
      className="active:bg-muted flex min-h-14 w-full flex-col gap-0.5 px-4 py-2.5 text-left disabled:opacity-60"
    >
      <span className={cn('flex items-start justify-between gap-3', corner && 'pr-16')}>
        <span className="text-foreground line-clamp-2 break-words text-sm font-semibold">
          {title}
        </span>
        {hint && <span className="text-foreground shrink-0 text-sm font-medium">{hint}</span>}
      </span>
      {lines
        .filter((line) => line !== '' && line !== null && line !== undefined && line !== false)
        .map((line, index) => (
          <span key={index} className="text-muted-foreground line-clamp-2 break-words text-sm">
            {line}
          </span>
        ))}
    </button>
    {corner && <div className="absolute right-2 top-1.5">{corner}</div>}
  </li>
);
